import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone, Input, EventEmitter, Output } from '@angular/core';
import { AuftragEx, AuftragspositionEx, AuftragspositionWiegungEx, WiegedatenEx } from 'src/app/api/model/model';
import { NavController, AlertController, ModalController, IonSelect } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Subscription, BehaviorSubject } from 'rxjs';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { MyApp } from 'src/app/api/helper/app';
import { Artikel, AdresseEntity, Auftragsposition, GpsPosition, MengeOptionTyp, Vertragsposition, WiegungTyp, Waage, Auftragstyp, Fahrzeug, WorkflowStatus, WiegenRequest, TextdateiTyp, Textdatei, Wiegedaten } from 'src/app/api/model/swagger-model';
import { TranslateService } from '@ngx-translate/core';
import { WaageService } from 'src/app/api/waage.service';
import { StammdatenService } from 'src/app/api/stammdaten.service';
import { SystemService } from 'src/app/api/system.service';
import { AppConfig } from 'src/app/api/helper/app.config';
import { i18n } from 'src/app/api/helper/i18n';
import { IAuftragdetails } from '../auftragdetails/iauftragdetails';
import { DateHelper } from 'src/app/api/helper/date-helper';
import { AuftragService } from 'src/app/api/auftrag.service';
import { Utils } from 'src/app/api/helper/utils';
import { Subject, firstValueFrom, timeout } from 'rxjs';
import { MobileWaage } from 'src/app/api/waagen/waage';
import { getWaageModusInfo } from 'src/app/api/waagen/waage-modus.helper';

const log = new Logger("AuftragspositionWiegenPage");

@Component({
    selector: 'app-auftragsposition-wiegen',
    templateUrl: './auftragsposition-wiegen.page.html',
    styleUrls: ['./auftragsposition-wiegen.page.scss'],
    standalone: false
})
export class AuftragspositionWiegenPage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('waagenSelect', { read: IonSelect, static: false }) waagenSelect: IonSelect;
    @ViewChild('wiegungenSelect', { read: IonSelect, static: false }) wiegungenSelect: IonSelect;

    @Input() auftrag: AuftragEx;
    @Input() auftragsposition: AuftragspositionEx;
    @Input() auftragdetails: IAuftragdetails;

    /**
     * Wird auf TRUE gesetzt, wenn die Komponente innerhalb des Wiegen-Popups angezeigt wird.
     * Dann soll beim Start der Wiegemodus auf der welvaarts-Waage aktiviert werden.
     */
    @Input() isPopup: boolean;

    @Output() changed: EventEmitter<any> = new EventEmitter();

    subscriptions: Subscription[] = [];

    title = new BehaviorSubject('Lieferung');

    einheiten = ['KG', 'Tonnen'];

    waagen: Waage[] = [];

    letzteWiegungDate = 0;
    wiegenNummer = 1;
    ausgewaehlteWaage: Waage;
    nfcRegistrationId: number;

    app = MyApp.current;

    wiegenHandeingabeErlaubt = false;
    wiegenLeergewichtErlaubt = false;

    /**
     * Wird auf TRUE gesetzt, wenn eine mobile Waage verwendet werden soll.
     * Anderenfalls wird eine stationäre Remote-Waage verwendet.
     * Das macht aber eigentlich nur Sinn bei Containerdiensten.
     */
    isMobileWaageVerwenden = false;

    /**
     * Wird auf TRUE gesetzt, wenn offene Wiegungen (z.B. über die Handtaster am Fahrzeug durchgeführt)
     * einer Auftragsposition zugeordnet werden dürfen.
     */
    isWiegungZuordnenErlaubt = false;

    /**
     * Die noch nicht zugeordneten Wiegungen. Wird für die Auswahl per Checkbox benötigt.
     */
    offeneWiegungen: Wiegedaten[] = [];

    isErstwiegungErfolgreich = false;
    isZweitwiegungErfolgreich = false;

    constructor(
        private alertController: AlertController,
        private modalController: ModalController,
        private auftragService: AuftragService,
        private cdr: ChangeDetectorRef,
        private systemService: SystemService,
        private route: ActivatedRoute,
        private ngZone: NgZone,
        private nav: NavController,
        private stammdatenService: StammdatenService,
        private waageService: WaageService,
        private translate: TranslateService) { }


    async ngOnInit() {
        log?.debug('ngOnInit', { auftragsposition: this.auftragsposition });
        this.aktualisiereWiegungenListe();

        if (this.auftrag.Auftragstyp === Auftragstyp.Transportauftrag) {
            this.wiegenHandeingabeErlaubt = AppConfig.current.einstellungen.TransportauftragPositionWiegenHandeingabe === 'ja';
            this.wiegenLeergewichtErlaubt = AppConfig.current.einstellungen.TransportauftragPositionWiegenLeergewicht === 'ja';
        } else if (this.auftrag.Auftragstyp === Auftragstyp.Hofchecker) {
            this.wiegenHandeingabeErlaubt = AppConfig.current.einstellungen.HofauftraegePositionWiegenHandeingabe === 'ja';
            this.wiegenLeergewichtErlaubt = AppConfig.current.einstellungen.HofauftraegePositionWiegenLeergewicht === 'ja';
        }

        // Entscheide ob eine mobile Waage verwendet werden soll oder eine Remote-Waage für den ganzen LKW.
        // Für OFK soll die mobile Waage verwendet werden.
        // Hier ist konfiguriert:
        // 1. WaageVerfuegbar = ja
        // 2. WaageModus = 'wiegen'
        const waageModus = getWaageModusInfo();

        this.isMobileWaageVerwenden = AppConfig.current.einstellungen.WaageVerfuegbar === 'ja' && waageModus.wiegenDurchApp;

        // Im Modus 'beides' kann die Wiegung aus der App erfolgen UND zusätzlich können Wiegungen zugeordnet werden,
        // die über die Handtaster am Fahrzeug durchgeführt wurden.
        this.isWiegungZuordnenErlaubt = this.isMobileWaageVerwenden && waageModus.zuordnen;

        if (this.isWiegungZuordnenErlaubt) {
            // Neue Wiegungen sollen sofort in der Liste erscheinen, auch wenn sie nicht von der App angefordert wurden.
            this.subscriptions.push(this.waageService.wiegedaten.subscribe(liste => {
                this.offeneWiegungen = liste ?? [];
                this.cdr.detectChanges();
            }));
        }

        if (this.isMobileWaageVerwenden && this.isPopup) {
            await this.aktiviereWiegemodus();
        }
    }

    ngAfterViewInit(): void {
        log?.debug('ngAfterViewInit');
    }

    ngOnDestroy(): void {
        log?.debug('ngOnDestroy');
        this.systemService.unregisterNfcReceiver(this.nfcRegistrationId);

        this.subscriptions.forEach(p => p.unsubscribe());
        this.subscriptions = [];

        if (this.isMobileWaageVerwenden) {
            const verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

            if (verbindungsmodus === 'nur-bei-bedarf') {
                // Trenne die Verbindung zur Waage.
                const waage = this.waageService.getAktiveWaage('Fahrzeug');

                if (waage) {
                    waage.close();
                }
            }
        }
    }

    onGewichtChanged(wiegung: AuftragspositionWiegungEx) {
        log?.debug('onGewichtChanged');
        this.aktualisiereVollstaendig();
        this.aktualisiereNetto();

        if (this.auftrag) {
            if (this.auftragdetails) {
                this.auftragdetails.starteAuftragWennErforderlich('Lieferposition Gewicht geändert');
            }

            // Hier soll sofort der Auftrag gespeichert werden.
            // Manchmal wird gewogen ohne dass die Auftragsposition danach verlassen wird.
            // Die Daten dürfen nicht verloren gehen wenn die App geschlossen wird.
            if (this.auftragService.istAktiverAuftrag(this.auftrag)) {
                this.auftragService.speichereAuftrag(this.auftrag);
            }
        }

        this.changed.next(null);
    }

    async onWaageSelected(e: Event) {
        log?.debug('onWaageSelected', e);

        if (!e) {
            return;
        }

        const event = e as CustomEvent;

        if (this.waagen && this.waagen.length == 1) {
            // Irgnorieren. Das event wurde nur ausgelöst weil die Property 'waagen' gesetzt wurde
            return;
        }

        this.doWiegen(event.detail.value);
    }

    private loescheWiegung(nummer: number) {
        const wiegung = this.getWiegung(nummer);

        wiegung.Erfolgreich = false;
        wiegung.Einheit = null;
        wiegung.Gewicht = null;
        wiegung.WiegeNr = null;
        wiegung.WaageKey = null;
        wiegung.Typ = WiegungTyp.Unbekannt;
        wiegung.Text = '';
    }

    getWiegung(nummer: number): AuftragspositionWiegungEx {
        switch (nummer) {
            case 1:
                return this.auftragsposition.Wiegungen2[0];

            case 2:
                return this.auftragsposition.Wiegungen2[1];

            default:
                throw new Error('Ungültige Nummer für Wiegung: ' + nummer);
        }
    }

    async onLeergewicht(nummer: number) {
        log?.debug('onLeergewicht: (' + nummer + ')');

        let fahrzeug: Fahrzeug;

        if (this.auftrag.Auftragstyp === Auftragstyp.Transportauftrag) {
            fahrzeug = MyApp.current.fahrzeug.getValue();

            if (!fahrzeug) {
                UiHelper.showAlert('Kein Fahrzeug geladen. Leergewicht unbekannt');
                return;
            }
        } else {
            // Fahrzeug des Auftrags suchen
            if (!this.auftrag.FahrzeugKennung) {
                UiHelper.showAlert('Kein Fahrzeug zum Auftrag definiert. Leergewicht unbekannt.');
                return;
            }

            fahrzeug = await this.stammdatenService.getFahrzeug(this.auftrag.FahrzeugKennung);

            if (!fahrzeug) {
                UiHelper.showAlert(`Fahrzeug '${this.auftrag.FahrzeugKennung}' nicht gefunden. Leergewicht unbekannt`);
                return;
            }
        }

        if (!fahrzeug.Leergewicht) {
            UiHelper.showAlert(`Kein Leergewicht für Fahrzeug ${fahrzeug.kennzeichen} konfiguriert`);
            return;
        }

        const wiegung = this.getWiegung(nummer);

        if (wiegung.Erfolgreich) {
            const ok = await UiHelper.confirmJaNein('Wiegung wirklich löschen?');

            if (!ok) {
                return;
            }
        }

        this.loescheWiegung(nummer);

        wiegung.Einheit = 'KG';
        wiegung.Gewicht = fahrzeug.Leergewicht;
        wiegung.WiegeNr = '****';
        wiegung.Typ = WiegungTyp.Leergewicht;
        wiegung.Text = `${wiegung.Gewicht} ${wiegung.Einheit}`;
        wiegung.Datum = Utils.nowIsoDateString();

        this.aktualisiereVollstaendig();

        if (this.auftrag) {
            if (this.auftragdetails) {
                this.auftragdetails.starteAuftragWennErforderlich('Lieferposition Leergewicht');
            }

            // Hier soll sofort der Auftrag gespeichert werden.
            // Manchmal wird gewogen ohne dass die Auftragsposition danach verlassen wird.
            // Die Daten dürfen nicht verloren gehen wenn die App geschlossen wird.
            if (this.auftragService.istAktiverAuftrag(this.auftrag)) {
                await this.auftragService.speichereAuftrag(this.auftrag);
            }
        }

        this.changed.next(null);
    }

    /**
     * Berechnet das Netto-Gewicht neu, oder löscht es falls noch keien zwei Wiegungen erfolgt sind
     */
    aktualisiereNetto() {
        this.auftragsposition.NettoGewicht = null;

        if (this.auftragsposition.Wiegungen2 && this.auftragsposition.Wiegungen2.length === 2) {
            const wiegung1 = this.auftragsposition.Wiegungen2[0];
            const wiegung2 = this.auftragsposition.Wiegungen2[1];

            if (wiegung1.Gewicht != null && wiegung2.Gewicht != null) {
                this.auftragsposition.NettoGewicht = Math.abs(wiegung1.Gewicht - wiegung2.Gewicht);
            }
        }
    }

    getWiegenButtonColor(nummer: number, typ: number): string {
        const wiegung = this.getWiegung(nummer);

        if (!wiegung.Typ) {
            wiegung.Typ = WiegungTyp.Unbekannt;
        }

        switch (typ) {
            case WiegungTyp.Wiegung:
                if (wiegung.Typ === WiegungTyp.Wiegung || wiegung.Typ === WiegungTyp.Unbekannt) {
                    if (wiegung.Erfolgreich) {
                        return 'light';
                    } else {
                        return 'primary';
                    }
                } else {
                    return 'light';
                }

            case WiegungTyp.Handeingabe:
                if (wiegung.Typ === WiegungTyp.Handeingabe) {
                    return 'primary';
                } else {
                    return 'light';
                }

            case WiegungTyp.Leergewicht:
                if (wiegung.Typ === WiegungTyp.Leergewicht) {
                    return 'primary';
                } else {
                    return 'light';
                }
        }

        return 'light';
    }

    async onWiegen(nummer: number) {
        log?.debug('onWiegen:  (' + nummer + ')');

        if (this.isMobileWaageVerwenden) {
            await this.doWiegenMitMobilerWaage(nummer);
        } else {
            await this.doWiegenMitRemoteWaage(nummer);
        }
    }

    /**
     * Führt die Waage mit einer mobilen Waage druch.
     */
    async doWiegenMitMobilerWaage(nummer: number) {
        const waage = this.waageService.getAktiveWaage('Fahrzeug');

        if (!waage) {
            UiHelper.showErrorLight('Keine Waage verbunden');
            return;
        }

        if (nummer === 1) {
            await this.doErstwiegungMitMobilerWaage(waage);
        } else if (nummer === 2) {
            await this.doZweitwiegungMitMobilerWaage(waage);
        }

        await this.auftragService.speichereAuftrag(this.auftrag);
    }

    /**
     * Führt die Erstwiegung mit einer mobilen Waage druch.
     */
    async doErstwiegungMitMobilerWaage(waage: MobileWaage) {
        MyApp.loading(true);

        try {
            this.isErstwiegungErfolgreich = false;
            this.isZweitwiegungErfolgreich = false;

            const ok = await waage.wiegen(1);

            if (!ok) {
                // Tritt auf, wenn die Waage den Wiegebefehl nicht akzeptiert.
                UiHelper.showErrorLight('Wiegen fehlgeschlagen');
                return;
            }

            this.isErstwiegungErfolgreich = true;
            this.isZweitwiegungErfolgreich = false;

            this.cdr.detectChanges();

        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);
        }
    }

    /**
     * Führt die Waage mit einer mobilen Waage druch.
     */
    async doZweitwiegungMitMobilerWaage(waage: MobileWaage) {
        MyApp.loading(true);

        try {
            // Sende den Wiegebefehl
            const ok = await waage.wiegen(2);

            if (!ok) {
                // Tritt auf, wenn die Waage den Wiegebefehl nicht akzeptiert.
                UiHelper.showErrorLight('Wiegen fehlgeschlagen');
                return;
            }

            if (!MyApp.isCordovaAvailable()) {
                setTimeout(() => {
                    waage.simuliereWiegedatenEmpfaengen();
                }, 2000);
            }

            // Diese Wiegung hat die App selbst angefordert. Sie wird direkt der Auftragsposition zugeordnet
            // und darf deshalb nicht zusätzlich in der Liste der offenen Wiegungen landen.
            this.waageService.wiegungWirdVonAppErwartet = true;

            let wiegedaten: WiegedatenEx;

            try {
                wiegedaten = await firstValueFrom(waage.wiegedatenEmpfangen.pipe(timeout(30_000)));
            } finally {
                this.waageService.wiegungWirdVonAppErwartet = false;
            }

            if (!wiegedaten) {
                UiHelper.showErrorLight('Keine Wiegedaten empfangen');
                return;
            }

            if (wiegedaten.errorMessage) {
                UiHelper.showErrorLight(wiegedaten.errorMessage);
                return;
            }


            if (!this.auftragsposition.Wiegungen) {
                this.auftragsposition.Wiegungen = [];
            }

            this.auftragsposition.Wiegungen.push(wiegedaten);

            // Die Sonderregel "Nullwiegung => 5 kg" (Welvaarts + Wiegung durch App) steckt in
            // berechneMengeAusWiegungen, damit sie auch bei erneuter Berechnung erhalten bleibt.
            this.auftragdetails.berechneMengeAusWiegungen(this.auftragsposition);

            // wiegung.Erfolgreich = true;
            // wiegung.Datum = Utils.nowIsoDateString();
            // wiegung.Text = Utils.formatFloat(wiegedaten.Wiegung1, 0) + ' kg';
            // wiegung.Gewicht = wiegedaten.Wiegung1;
            // wiegung.Nummer = nummer;
            // wiegung.WiegeNr = wiegedaten.Wiegenummer?.toString();
            // wiegung.Typ = WiegungTyp.Wiegung;

            this.aktualisiereNetto();

            this.auftragdetails.detectChanges.next('doWiegenMitMobilerWaage');

            this.isZweitwiegungErfolgreich = true;

            this.cdr.detectChanges();

        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);
        }

        this.aktualisiereVollstaendig();

        this.changed.next(null);
    }

    /**
     * Öffnet die Auswahl der noch nicht zugeordneten Wiegungen.
     * Das sind die Wiegungen, die nicht von der App angefordert wurden, z.B. über die Handtaster am Fahrzeug.
     */
    async onWiegungenZuordnen() {
        log?.debug('onWiegungenZuordnen');

        const wiegedatenListe = this.waageService.wiegedaten.getValue();

        if (!wiegedatenListe || !wiegedatenListe.length) {
            UiHelper.showErrorLight('Keine Wiegungen vorhanden');
            return;
        }

        if (!this.auftragsposition.Wiegungen) {
            this.auftragsposition.Wiegungen = [];
        }

        this.offeneWiegungen = wiegedatenListe;
        this.cdr.detectChanges();

        setTimeout(() => {
            this.wiegungenSelect.open();
        });
    }

    /**
     * Die ausgewählten Wiegungen werden der Auftragsposition zugeordnet.
     * Nicht ausgewählte Wiegungen bleiben in der Liste und stehen auch im nächsten Auftrag noch zur Verfügung.
     */
    async onWiegungSelected(e: Event) {
        log?.debug('onWiegungSelected', e);

        try {
            const event = e as CustomEvent;

            if (!this.auftragsposition.Wiegungen) {
                this.auftragsposition.Wiegungen = [];
            }

            const wiegedatenListe = this.waageService.wiegedaten.getValue();
            const ausgewaehlteWiegungen = event.detail.value as Wiegedaten[];

            if (ausgewaehlteWiegungen?.length) {
                for (const w of ausgewaehlteWiegungen) {
                    const idx = wiegedatenListe.indexOf(w);

                    if (idx >= 0) {
                        // Entferne Eintrag aus den verfügbaren Wiegungen
                        wiegedatenListe.splice(idx, 1);

                        // Und zur Auftragsposition hinzufügen
                        this.auftragsposition.Wiegungen.push(w);
                    }
                }

                await this.waageService.setzeWiegedatenListe(wiegedatenListe);

                this.auftragdetails.berechneMengeAusWiegungen(this.auftragsposition);

                this.aktualisiereNetto();

                await this.auftragService.speichereAuftrag(this.auftrag);

                this.auftragdetails.detectChanges.next('onWiegungSelected');
            }
        } catch (err) {
            UiHelper.showError(err);
        }

        this.aktualisiereVollstaendig();

        this.cdr.detectChanges();

        this.changed.next(null);
    }

    /**
     * Summe der aktuell zugeordneten Wiegungen (Netto).
     */
    getSummeNetto(): number {
        if (!this.auftragsposition.Wiegungen) {
            return 0;
        }

        let summe = 0;

        for (const w of this.auftragsposition.Wiegungen) {
            if (w.Netto) {
                summe += Utils.parseFloat(w.Netto);
            }
        }

        return Utils.round(summe, 1);
    }

    /**
     * Mobile Wiegung entfernen
     */
    async onWiegungEntfernen(wiegung: Wiegedaten) {
        log?.info('onWiegungEntfernen: ', { wiegung });

        if (!this.auftragsposition.Wiegungen) {
            this.auftragsposition.Wiegungen = [];
        }

        const idx = this.auftragsposition.Wiegungen.indexOf(wiegung);

        if (idx >= 0) {
            // Wiegung aus Auftragsposition entfernen
            this.auftragsposition.Wiegungen.splice(idx, 1);

            // Wiegung zu den bekannten Wiegungen hinzufügen
            const wiegedatenListe = this.waageService.wiegedaten.getValue();
            wiegedatenListe.push(wiegung);
            this.waageService.setzeWiegedatenListe(wiegedatenListe);

            this.auftragdetails.berechneMengeAusWiegungen(this.auftragsposition);
        }

        await this.auftragService.speichereAuftrag(this.auftrag);

        this.auftragdetails.detectChanges.next('onWiegungEntfernen');

        this.cdr.detectChanges();
    }

    /**
     * Führt die Wiegung mit einer stationäeren Remote-Waage durch.
     */
    async doWiegenMitRemoteWaage(nummer: number) {
        const wiegung = this.getWiegung(nummer);

        if (wiegung.Erfolgreich) {
            const ok = await UiHelper.confirmJaNein('Wirklich neu wiegen?');

            if (!ok) {
                return;
            }
        }

        this.loescheWiegung(nummer);

        await UiHelper.showLoading('Suche Waagen');

        const waagen = await this.waageService.getRemoteWaagenInReichweite();

        if (!waagen || !waagen.length) {
            UiHelper.showAlert('Keine Waage in Reichweite gefunden', 'Wiegen');
            return;
        }

        this.waagen = waagen;
        this.wiegenNummer = nummer;

        if (waagen.length == 1) {
            this.doWiegen(waagen[0]);
        } else {
            this.cdr.detectChanges();

            await UiHelper.hideLoading();

            setTimeout(() => {
                this.waagenSelect.open();
            });
        }
    }

    async onHandeingabe(nummer: number) {
        log?.debug('onHandeingabe:  (' + nummer + ')');

        const wiegung = this.getWiegung(nummer);

        if (wiegung.Erfolgreich) {
            const ok = await UiHelper.confirmJaNein('Wiegung wirklich löschen?');

            if (!ok) {
                return;
            }
        }

        this.loescheWiegung(nummer);

        wiegung.Typ = WiegungTyp.Handeingabe;
        wiegung.Einheit = 'KG';
        wiegung.Gewicht = null;
        wiegung.WiegeNr = '****';
        wiegung.Datum = Utils.nowIsoDateString();

        this.cdr.detectChanges();

        const id = `wiegung-${this.auftragsposition.PosNr}-${wiegung.Nummer}-input`;

        this.aktualisiereVollstaendig();

        setTimeout(() => {
            const e = document.getElementById(id);
            if (e) {
                const input = e.getElementsByTagName('input')[0]
                if (input) {
                    input.focus();
                }
            }
        }, 100);

        this.changed.next(null);
    }

    async doWiegen(waage: Waage) {
        log?.debug('doWiegen', waage);

        if (!waage) {
            return;
        }

        if (this.letzteWiegungDate > Date.now() - 1000) {
            log.warn('doWiegen wurde zu schnell hintereinander aufgerufen. Max 1 Aufruf je 1000 ms erlaubt');
            return;
        }

        this.letzteWiegungDate = Date.now();
        this.ausgewaehlteWaage = waage;

        let sendenWiegung: AuftragspositionWiegungEx = null;

        try {
            await UiHelper.showLoading('Wiegung läuft...');

            const request: WiegenRequest = {
                WaageKey: waage.Key,
                Auftragsnummer: this.auftrag.Auftragsnummer,
                PositionNummer: this.auftragsposition.PosNr,
                PositionArtikelKey: this.auftragsposition.ArtikelKey,
                PositionBezeichnung: this.auftragsposition.Bezeichnung,
            };

            if (this.wiegenNummer == 1) {
                request.Wiegeart = 'Erstwiegung';
            } else if (this.wiegenNummer == 2) {
                request.Wiegeart = 'Zweitwiegung';
            }

            const wiegenResult = await this.waageService.wiegenRemote(request);

            await UiHelper.hideLoading();

            const wiegung = this.getWiegung(this.wiegenNummer);

            if (wiegenResult.Success) {
                wiegung.Typ = WiegungTyp.Wiegung;
                wiegung.Datum = wiegenResult.Datum;
                wiegung.Gewicht = wiegenResult.Gewicht;
                wiegung.WiegeNr = wiegenResult.WiegeNr;
                wiegung.WaageKey = wiegenResult.WaageKey;
                wiegung.Einheit = "KG";
                wiegung.Erfolgreich = true;

                let uhrzeit = DateHelper.formatUhrzeit(wiegung.Datum);

                wiegung.Text = `${wiegung.Gewicht} KG, Nr. ${wiegung.WiegeNr}, Waage: ${wiegung.WaageKey}, ${uhrzeit} Uhr`

                sendenWiegung = wiegung;

                this.aktualisiereNetto();
            } else {
                UiHelper.showError(wiegenResult.Message, 'Fehler', false, false);
            }

            this.cdr.detectChanges();
        } catch (err) {
            UiHelper.showError(err);
        }

        this.aktualisiereVollstaendig();

        if (this.auftrag) {
            if (this.auftragdetails) {
                this.auftragdetails.starteAuftragWennErforderlich('Lieferposition Wiegung');
            }

            // Hier soll sofort der Auftrag gespeichert werden.
            // Manchmal wird gewogen ohne dass die Auftragsposition danach verlassen wird.
            // Die Daten dürfen nicht verloren gehen wenn die App geschlossen wird.
            if (this.auftragService.istAktiverAuftrag(this.auftrag)) {
                await this.auftragService.speichereAuftrag(this.auftrag);
            }

            if (sendenWiegung && AppConfig.current.einstellungen.TransportauftragPositionWiegenSofortSenden === 'ja') {
                await UiHelper.showLoading('Wiegung wird gesendet...');

                try {
                    // Austragstatus automatisch senden
                    // Die Wiegungen sollen in ReCoLog direkt sichtbar sein
                    const status: WorkflowStatus = {
                        anmerkungErforderlich: false,
                        auftragAbschliessen: false,
                        auftragsdatenSenden: true,
                        bildErforderlich: false,
                        bilderSenden: false,
                        GeopositionExaktErfassen: false,
                        geopositionSenden: false,
                        minAnzahlBilder: 0,
                        unterschriftErforderlich: false,
                        unterschriftSenden: false,
                        key: AppConfig.current.einstellungen.TransportauftragPositionWiegenSofortSendenStatus,
                        name: `Wiegung ${sendenWiegung.Gewicht} KG, Nr. ${sendenWiegung.WiegeNr}`
                    };

                    await this.auftragService.statusmeldung(this.auftrag, status)
                } catch (err) {
                    log.error('Fehler beim Senden der automatischen Statusmeldung nach Wiegen: ' + Utils.getErrorMessage(err), err);
                }

                await UiHelper.hideLoading();
            }
        }

        this.changed.next(null);
    }

    aktualisiereVollstaendig() {
        this.aktualisiereWiegungenListe();
        this.aktualisiereNetto();
    }

    aktualisiereWiegungenListe() {
        if (!this.auftragsposition.Wiegungen2) {
            this.auftragsposition.Wiegungen2 = [];
        }

        // Es muss mindestens eine Wiegung
        if (this.auftragsposition.Wiegungen2.length < 1) {
            this.auftragsposition.Wiegungen2.push({
                Nummer: 1,
                Typ: WiegungTyp.Unbekannt
            });
        }

        if (this.auftragsposition.Wiegungen2.length === 1) {
            if (this.auftragsposition.Wiegungen2[0].Gewicht || this.auftragsposition.Wiegungen2[0].Gewicht === 0) {
                this.auftragsposition.Wiegungen2.push({
                    Nummer: 2,
                    Typ: WiegungTyp.Unbekannt
                });
            }
        } else if (this.auftragsposition.Wiegungen2.length === 2) {
            if (!this.auftragsposition.Wiegungen2[0].Gewicht && this.auftragsposition.Wiegungen2[0].Gewicht !== 0) {
                this.auftragsposition.Wiegungen2.splice(1, 1);
            }
        }

        for (const wiegung of this.auftragsposition.Wiegungen2) {
            wiegung.Bezeichnung = i18n('AuftragWiegung' + wiegung.Nummer);
        }

        // if (this.auftragsposition.Menge) {
        // } else {
        //     this.auftragsposition.Wiegungen2 = [];
        // }
    }

    /**
     * Aktiviert wen Wiegemodus auf der mobilen Waage.
     */
    async aktiviereWiegemodus() {
        try {
            const waage = this.waageService.getAktiveWaage('Fahrzeug');

            if (!waage) {
                UiHelper.showErrorLight('Keine aktive mobile Waage verbunden');
                return;
            }

            MyApp.loading(true);

            // Verbinden und Wiegemodus aktivieren (falls verfügbar)
            await waage.pruefeVerbindung();

            // Aktiviere den Wiegemodus.
            await waage.aktiviereWiegemodus();
        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);
        }
    }
}

interface AuftragspositionVO {
    auftragsposition: Auftragsposition;
    artikel: Artikel;
    vertragsposition?: Vertragsposition;
    behaelter: BehaelterbewegungVO[];
    mengeUngueltig: boolean;
    MengeOption: MengeOptionTyp;
    StandardMenge: number;
    wiegungZuordnenAnzeigen: boolean;
    LaderaumAuswahlAktiv: boolean;
    suchtext: string;
}

interface BehaelterbewegungVO {
    name: string;
    aktion: 'stellen' | 'entleeren' | 'abziehen' | 'umsetzen' | 'direktbeladung';
    behaelterNr: string;
    manuelleNummer?: string;
    behaelterNrUngueltig?: boolean;
    isScan?: boolean;

    // Zum anzeigen von "NFC aktiv"
    aktiv?: boolean;

    // Soll der Abstellort ausgewählt werden?
    abstellortAktiv?: boolean;

    // Nur temporär fpr ionic-selectable
    abstellort?: AdresseEntity;

    // Adresse des Abstellorts
    abstellortKey?: string;
    zustand?: string; // voll / leer

    position?: GpsPosition;

    changed?: boolean;
}

interface LaderaumVO {
    key: string;
    bezeichnung: string;
}

